import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { map, find } from 'lodash';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { linkIntegrityCheck } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import { Confirm, Dimmer, Loader, Message } from 'semantic-ui-react';

const messages = defineMessages({
  cutConfirmSingleItem: {
    id: 'Cut this item?',
    defaultMessage: 'Cut this item?',
  },
  cutConfirmMultipleItems: {
    id: 'Cut selected items?',
    defaultMessage: 'Cut selected items?',
  },
  loading: {
    id: 'link-integrity: loading',
    defaultMessage: 'Checking...',
  },
  cut: {
    id: 'link-integrity: Cut',
    defaultMessage: 'Cut',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
});
const CUTTING_LIMIT = 300;

const ContentsCutModal = (props) => {
  const { itemsToCut = [], open, onCancel, onOk, items } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const linkintegrityInfo = useSelector((state) => state.linkIntegrity?.result);
  const loading = useSelector((state) => state.linkIntegrity?.loading);

  const [containedItemsToCut, setContainedItemsToCut] = useState([]);

  //check integrity
  useEffect(() => {
    const getFieldById = (id, field) => {
      const item = find(items, { '@id': id });
      return item ? item[field] : '';
    };

    if (itemsToCut.length > 0 && open) {
      dispatch(
        linkIntegrityCheck(
          map(itemsToCut, (item) => getFieldById(item, 'UID')),
        ),
      );
    }
  }, [itemsToCut, items, open, dispatch]);

  //handle linkintegrity response
  useEffect(() => {
    if (linkintegrityInfo) {
      const containedItems = linkintegrityInfo
        .map((result) => result.items_total ?? 0)
        .reduce((acc, value) => acc + value, 0);
      setContainedItemsToCut(containedItems);
    } else {
      setContainedItemsToCut([]);
    }
  }, [linkintegrityInfo]);

  return (
    open && (
      <Confirm
        open={open}
        confirmButton={intl.formatMessage(messages.cut)}
        cancelButton={intl.formatMessage(messages.cancel)}
        header={
          itemsToCut.length === 1
            ? intl.formatMessage(messages.cutConfirmSingleItem)
            : intl.formatMessage(messages.cutConfirmMultipleItems)
        }
        content={
          <div className="content">
            <Dimmer active={loading} inverted>
              <Loader indeterminate size="massive">
                {intl.formatMessage(messages.loading)}
              </Loader>
            </Dimmer>

            {containedItemsToCut > 0 ? (
              <>
                <FormattedMessage
                  id="{variationSelectedItems} you will cut {containedItemsToCut} total {variation}."
                  defaultMessage="{variationSelectedItems} you will cut {containedItemsToCut} total {variation}."
                  values={{
                    containedItemsToCut: <span>{containedItemsToCut}</span>,
                    variation: (
                      <span>
                        {containedItemsToCut === 1 ? (
                          <FormattedMessage id="item" defaultMessage="item" />
                        ) : (
                          <FormattedMessage id="items" defaultMessage="items" />
                        )}
                      </span>
                    ),
                    variationSelectedItems: (
                      <>
                        {itemsToCut.length == 1 ? (
                          <FormattedMessage
                            id="This item is also a folder. By cutting it"
                            defaultMessage="This item is also a folder. By cutting it"
                          />
                        ) : (
                          <FormattedMessage
                            id="Some items are also a folder. By cutting them"
                            defaultMessage="Some items are also a folder. By cutting them"
                          />
                        )}
                      </>
                    ),
                  }}
                />
              </>
            ) : null}

            {containedItemsToCut > CUTTING_LIMIT ? (
              <Message warning>
                <FormattedMessage
                  id="You are cutting more then {cutting_limit} items. It's recommended to contact administrators to do this operation."
                  defaultMessage="You are cutting more then {cutting_limit} items. It's recommended to contact administrators to do this operation."
                  values={{ cutting_limit: CUTTING_LIMIT }}
                />
              </Message>
            ) : null}
          </div>
        }
        onCancel={onCancel}
        onConfirm={onOk}
        size="medium"
      />
    )
  );
};

ContentsCutModal.propTypes = {
  itemsToCut: PropTypes.arrayOf(
    PropTypes.shape({
      UID: PropTypes.string,
    }),
  ).isRequired,
  open: PropTypes.bool.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
export default ContentsCutModal;
